import Immutable from 'immutable';
import PropTypes from 'prop-types';
import React from 'react';
import cn from 'classnames';
import { FormattedMessage } from 'react-intl';

import AvailabilityStatus from '@bibliocommons/availability-status';
import {
  IconButton,
  SecondaryGhostButton,
  SecondarySolidButton,
  SecondaryTextButton
} from '@bibliocommons/base-buttons';
import {
  Edit as EditIcon,
  FlagOutline as FlagOutlineIcon,
  Heart as LikedIcon,
  HeartOutline as NotLikedIcon,
  Info as InfoIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  Website as IconWebsite
} from '@bibliocommons/base-icons';
import { ChevronLink } from '@bibliocommons/base-links';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { libraryShape, userIdentityShape } from '@bibliocommons/bc-prop-types';
import { ExpandableText } from '@bibliocommons/expandable';
import Heading from '@bibliocommons/heading';
import { Modal, ModalContent, ModalFooter, ModalHeader } from '@bibliocommons/modal';

import AuthorLink from 'app/components/shared/AuthorLink';
import ShortFormattedDate from 'app/components/shared/ShortFormattedDate';
import BibJacket from 'app/components/bibs/shared/BibJacket';
import { LIST_ITEM_TYPE, LIST_VISIBILITY_TYPE } from 'app/constants/list';
import { extractDomain, mapListTypeToMessageId } from 'app/helpers/list';
import UgcLikesButtonContainer from 'app/components/bibs/shared/ugc/UgcLikesButton/UgcLikesButtonContainer';
import { intlShape, displayBibShape, listItemShape, listPaginationShape, listShape } from 'app/shapes';
import displayAvailabilityShape from 'app/shapes/displayAvailability';

import './ListPage.scss';

const ListPage = ({
  availabilityByMetadataId,
  bibsByMetadataId,
  closeInfoModal,
  currentLibrary,
  currentUser,
  handleCopyCoversClick,
  handleFlagClick,
  handleInfoClick,
  handleLikeClick,
  handleLoadMoreItemsClick,
  handlePrintClick,
  handleShareClick,
  handleViewFormatsClick,
  intl,
  isFetchingItems,
  isInfoModalOpen,
  isLikedByCurrentUser,
  isStaffList,
  isMobileApp,
  items = [],
  list,
  manifestationsByMetadataId,
  numLikes,
  pageTitle,
  pagination,
  renderAddToShelfButton,
  renderUserCard,
  trackBibAuthorClick,
  trackBibImageClick,
  trackItemTitleClick,
  trackRelatedBibAuthorClick,
  trackRelatedBibTitleClick,
  trackWebUrlClick
}) => {
  const {
    createdTimestamp,
    description,
    imageUrl: listImageUrl,
    isRanked = false,
    listId,
    relatedMetadataId,
    type: listType,
    userId,
    updatedTimestamp,
    visibilityType
  } = list;

  const isCurrentUserTheCreator = userId === String(currentUser?.get('id'));
  const isCurrentUserFromStaff = currentUser?.get('staff') ?? false;
  const isShowLoadMoreItemsButton = pagination.page !== pagination.pages;
  const isVisibleByLinkOnly = visibilityType === LIST_VISIBILITY_TYPE.LINK;
  const hasDescription = Boolean(description);
  const hasSomeLikes = numLikes > 0;
  const ListTag = isRanked ? 'ol' : 'ul';
  const relatedBib = bibsByMetadataId[relatedMetadataId] || null;
  const hasRelatedBibAuthors = (relatedBib?.authors?.length || 0) > 0;
  const numItemsLoaded = Math.min(pagination.page * pagination.limit, pagination.count);
  const numItemsInNextPage = Math.min(pagination.limit, pagination.count - numItemsLoaded);
  const className = cn('cp-list-page', 'container', { 'cp-list-page--mobile-app': isMobileApp });
  const relatedBibUrl = relatedBib ? `/v2/record/${relatedBib.metadataId}` : null;
  const renderedUserCard = renderUserCard();
  const likesButtonText = intl.formatMessage(
    {
      defaultMessage: `${numLikes} ${numLikes === 1 ? 'user likes' : 'users like'} this`,
      id: 'list.label_num_likes'
    },
    { numLikes }
  );

  return (
    <div className={className}>
      <Modal handleClose={closeInfoModal} key="info-modal" open={isInfoModalOpen}>
        <ModalHeader
          title={intl.formatMessage({
            defaultMessage: 'About Lists',
            id: 'list.about.title'
          })}
        />
        <ModalContent>
          <p>
            <FormattedMessage defaultMessage="This List was created by" id="list.about.text_user" />
          </p>
          {renderedUserCard && <div className="user-card">{renderedUserCard}</div>}
          <p className="dates-info">
            <FormattedMessage defaultMessage="Created: " id="list.about.created" />
            <ShortFormattedDate value={createdTimestamp} />
            <br />
            <FormattedMessage defaultMessage="Last published: " id="list.about.updated" />
            <ShortFormattedDate value={updatedTimestamp} />
          </p>
          <p>
            <FormattedMessage
              defaultMessage={`Lists reflect the opinions of contributing users. Their title, description, and annotations do not
              represent the opinions of ${currentLibrary.get('longName', 'The Public Library')}.`}
              id="list.about.text_about"
              values={{ currentLibraryLongName: currentLibrary.get('longName', 'The Public Library') }}
            />
          </p>
          <p>
            <a href="https://help.bibliocommons.com/030lists">
              <FormattedMessage defaultMessage="Learn more about Lists" id="list.about.label_learn_more" />
            </a>
          </p>
        </ModalContent>
        <ModalFooter
          handlePrimaryAction={closeInfoModal}
          primaryActionButtonTag={SecondarySolidButton}
          primaryActionButtonText={intl.formatMessage({
            id: 'button_close'
          })}
          renderSecondaryAction={() => null}
        />
      </Modal>

      <div className="row">
        <div className="col-12 actions-container">
          <div className="actions">
            <IconButton className="icon-info--mobile" dataKey="info-button" handleClick={handleInfoClick}>
              <InfoIcon />
              <ScreenReaderMessage>
                <FormattedMessage defaultMessage="About Lists" id="list.icon_about" />
              </ScreenReaderMessage>
            </IconButton>
            <IconButton dataKey="flag-button" handleClick={handleFlagClick}>
              <FlagOutlineIcon />
              <ScreenReaderMessage>
                <FormattedMessage defaultMessage="Flag this list" id="list.icon_flag" />
              </ScreenReaderMessage>
            </IconButton>
            <IconButton dataKey="print-button" handleClick={handlePrintClick}>
              <PrintIcon />
              <ScreenReaderMessage>
                <FormattedMessage defaultMessage="Print this list" id="list.icon_print" />
              </ScreenReaderMessage>
            </IconButton>
            {!isMobileApp && (
              <IconButton dataKey="share-button" handleClick={handleShareClick}>
                <ShareIcon />
                <ScreenReaderMessage>
                  <FormattedMessage defaultMessage="Share this list" id="list.icon_share" />
                </ScreenReaderMessage>
              </IconButton>
            )}
          </div>
        </div>
      </div>

      <header className="header row">
        <div
          className={cn('header-left', {
            'header-left--with-image': Boolean(listImageUrl)
          })}
        >
          <div className="tags-container">
            <ul className="tags-list">
              {listType && (
                <li className="tags-item">{intl.formatMessage({ id: mapListTypeToMessageId(listType) })}</li>
              )}
              {isStaffList ? (
                <li className="tags-item tags-item--staff">
                  <FormattedMessage defaultMessage="Staff-Created List" id="list.label_created_staff" />
                </li>
              ) : (
                <li className="tags-item">
                  <FormattedMessage defaultMessage="Patron-Created List" id="list.label_created_patron" />
                </li>
              )}
            </ul>

            <IconButton className="icon-info--desktop" dataKey="info-button" handleClick={handleInfoClick}>
              <InfoIcon />
              <ScreenReaderMessage>
                <FormattedMessage defaultMessage="About Lists" id="list.icon_about" />
              </ScreenReaderMessage>
            </IconButton>
          </div>

          <Heading className="heading" size="large" tagName="h1" variant>
            <span className="heading__title">
              {relatedBibUrl ? (
                <a href={relatedBibUrl} onClick={() => trackRelatedBibTitleClick({ linkText: pageTitle })}>
                  {pageTitle}
                </a>
              ) : (
                pageTitle
              )}
            </span>
            <span className="heading__actions">
              {!isMobileApp && isCurrentUserTheCreator && (
                <a className="heading__action" href={`/user_lists/${list.listId}/edit`}>
                  <EditIcon />
                  <ScreenReaderMessage>
                    <FormattedMessage defaultMessage="Edit this list" id="list.icon_edit" />
                  </ScreenReaderMessage>
                </a>
              )}

              <IconButton className="heading__action" dataKey="like-button" handleClick={handleLikeClick}>
                {isLikedByCurrentUser ? (
                  <>
                    <LikedIcon />
                    <ScreenReaderMessage>
                      <FormattedMessage defaultMessage="Unlike this list" id="list.icon_unlike" />
                    </ScreenReaderMessage>
                  </>
                ) : (
                  <>
                    <NotLikedIcon />
                    <ScreenReaderMessage>
                      <FormattedMessage defaultMessage="Like this list" id="list.icon_like" />
                    </ScreenReaderMessage>
                  </>
                )}
              </IconButton>

              {isCurrentUserTheCreator && isVisibleByLinkOnly && (
                <SecondaryTextButton
                  className="heading__action heading__action--copy-list-link"
                  dataKey="copy-list-link-button"
                  handleClick={handleShareClick}
                >
                  <FormattedMessage defaultMessage="Copy List URL" id="list.copy_link_button_label" />
                </SecondaryTextButton>
              )}
            </span>
          </Heading>

          {hasRelatedBibAuthors && (
            <div className="authors">
              <AuthorLink
                authors={relatedBib.authors}
                trackAuthorClick={({ author }) => trackRelatedBibAuthorClick({ author, bib: relatedBib })}
              />
            </div>
          )}

          {hasSomeLikes && (
            <span className="total-likes">
              <UgcLikesButtonContainer
                buttonText={likesButtonText}
                buttonType="primary"
                contentId={parseInt(listId, 10)}
                count={numLikes}
              />
            </span>
          )}

          {hasDescription && (
            <p className="description">
              {relatedBib &&
                intl.formatMessage({
                  defaultMessage: "You'll enjoy these titles because: ",
                  id: 'list.label_related_description_prefix'
                })}
              {description}
            </p>
          )}

          {renderedUserCard && <div className="header-user-card">{renderedUserCard}</div>}

          {isCurrentUserTheCreator && !isMobileApp && (
            <ChevronLink className="view-my-lists" dataKey="view-my-lists" href={`/lists/show/${userId}`}>
              <FormattedMessage defaultMessage="View My Lists" id="list.action_view_my_lists" />
            </ChevronLink>
          )}
        </div>

        {listImageUrl && (
          <div className="header-right">
            <img alt={currentLibrary.get('longName')} className="header-print-logo" src={currentLibrary.get('logo')} />

            <div className="header-image__wrapper">
              {isCurrentUserFromStaff && !isMobileApp && (
                <div className="copy-covers-container">
                  <SecondaryGhostButton
                    className="copy-covers"
                    dataKey="copy-image-button"
                    handleClick={handleCopyCoversClick}
                  >
                    <FormattedMessage defaultMessage="Copy covers" id="list.action_copy_covers" />
                  </SecondaryGhostButton>
                </div>
              )}
              <img alt="" src={listImageUrl} />
            </div>
          </div>
        )}
      </header>

      <section className="row">
        <div className="main col-12">
          <p className="count">
            <FormattedMessage
              defaultMessage={`${pagination.count} ${pagination.count === 1 ? 'item' : 'items'}`}
              id="list.label_item_count"
              values={{ numItemsTotal: pagination.count }}
            />
          </p>
          {Boolean(items.length) && (
            <ListTag className="items">
              {items.map((item, index) => {
                // TODO: add origin param to creator URL
                const { annotation, itemId, metadataId, title: webTitle, type, url: webUrl } = item;

                const bib = bibsByMetadataId?.[metadataId] ?? null;
                const manifestations = manifestationsByMetadataId?.[metadataId] ?? [];
                const {
                  authors = [],
                  callNumber,
                  formatId,
                  imageUrl,
                  publicationDate,
                  publicationStatement,
                  subtitle,
                  title: bibTitle
                } = bib ?? {};

                const additionalPrintInfo = ((bibCallNumber, bibPublicationStatement) => {
                  if (!bibCallNumber) {
                    return bibPublicationStatement;
                  }
                  if (!bibPublicationStatement) {
                    return bibCallNumber;
                  }

                  return `${bibPublicationStatement} — ${bibCallNumber}`;
                })(callNumber, publicationStatement);

                const availability = availabilityByMetadataId[metadataId] || null;
                const isWebResource = type === LIST_ITEM_TYPE.WEB;
                const formatLocalizationId = `FORMAT.${(formatId || 'default').toLowerCase()}`;
                const localizedFormat = intl.formatMessage({ id: formatLocalizationId });
                const localizedFormatWithDate = `${localizedFormat}, ${publicationDate}`;
                const hasAddToShelfButton = !isWebResource;
                const hasAlternativeFormatManifestations = manifestations.length > 0;
                const hasAvailabilityStatus = Boolean(availability);
                const hasAuthors = !isWebResource && (authors ?? []).length > 0;
                const hasSubtitle = Boolean(subtitle);
                const position = index + 1;
                const itemTitle = isWebResource ? webTitle : bibTitle;
                const source = extractDomain(webUrl);
                const titleLanguageCode = isWebResource ? null : bib.languageCode;
                const titleUrl = isWebResource ? webUrl : `/v2/record/${metadataId}`;
                const viewFormatAvailabilityText = intl.formatMessage(
                  {
                    defaultMessage: `View format availability (${manifestations.length})`,
                    id: 'list.action_view_format_availability'
                  },
                  { numFormats: manifestations.length }
                );

                return (
                  <li className="item" data-key={itemId} key={itemId}>
                    <article className="item__inner">
                      <div className="item__inner-right">
                        <header className="item__header">
                          <div className="item__header-inner">
                            <h2 className="item__title">
                              <a
                                className="item__title-link"
                                href={titleUrl}
                                lang={titleLanguageCode}
                                onClick={() => trackItemTitleClick({ item, linkText: itemTitle, position })}
                              >
                                {isRanked ? `${position}. ` : null}
                                {itemTitle}
                              </a>
                            </h2>
                            {webUrl && (
                              <a
                                className="item__web-url"
                                href={webUrl}
                                onClick={() => trackWebUrlClick({ item, linkText: source, position })}
                              >
                                <FormattedMessage id="view_on_source" values={{ source }} />
                              </a>
                            )}
                            {hasSubtitle && <p className="item__subtitle">{subtitle}</p>}
                            {hasAuthors && (
                              <AuthorLink
                                authors={authors}
                                trackAuthorClick={({ author }) =>
                                  trackBibAuthorClick({ bib, linkText: author, position })}
                              />
                            )}
                          </div>
                          {hasAddToShelfButton && (
                            <span className="item__add-to-shelf">{renderAddToShelfButton(metadataId)}</span>
                          )}
                        </header>

                        {annotation && (
                          <ExpandableText
                            className="item__annotation"
                            text={annotation}
                            truncateCount={250}
                            textKey="annotation"
                          />
                        )}

                        <footer className="item__footer">
                          {hasAvailabilityStatus && (
                            <span className="item__availability">
                              <AvailabilityStatus
                                availabilityLocationType={availability.locationType}
                                rollUpStatus={availability.rollupStatus}
                                status={availability.localizedStatus}
                              />
                            </span>
                          )}
                          <span className="item__resource-type">
                            {isWebResource
                              ? intl.formatMessage({
                                  defaultMessage: 'Web resource',
                                  id: 'list.label_web_resource'
                                })
                              : localizedFormatWithDate}
                          </span>
                          {hasAlternativeFormatManifestations && (
                            <button
                              className="item__view-alt-formats"
                              onClick={() => {
                                handleViewFormatsClick({ linkText: viewFormatAvailabilityText, metadataId, position });
                              }}
                              type="button"
                            >
                              {viewFormatAvailabilityText}
                            </button>
                          )}
                          {additionalPrintInfo && (
                            <span className="item__additional-print-info">{additionalPrintInfo}</span>
                          )}
                        </footer>
                      </div>
                      <div className="item__inner-left">
                        {isWebResource ? (
                          <div className="item__icon-container">
                            <IconWebsite className="item__icon-website" />
                          </div>
                        ) : (
                          <BibJacket
                            cover={Immutable.Map({ large: imageUrl })}
                            format={formatId}
                            handleClick={() => trackBibImageClick({ item, position })}
                            metadataId={metadataId}
                            renderAsLink
                          />
                        )}
                      </div>
                    </article>
                  </li>
                );
              })}
            </ListTag>
          )}
        </div>

        <div className="pagination">
          <p>
            <FormattedMessage
              defaultMessage={`You've viewed ${numItemsLoaded} of ${pagination.count} ${
                pagination.count === 1 ? 'item' : 'items'
              }`}
              id="list.label_pagination_count"
              values={{ numItemsLoaded, numItemsTotal: pagination.count }}
            />
          </p>
          {isShowLoadMoreItemsButton && (
            <SecondaryGhostButton
              className="load-more"
              dataKey="load-more"
              handleClick={handleLoadMoreItemsClick}
              isLoading={isFetchingItems}
              size="large"
              type="button"
            >
              <FormattedMessage defaultMessage="Show next" id="list.action_load_more" values={{ numItemsInNextPage }} />
            </SecondaryGhostButton>
          )}
        </div>
      </section>
    </div>
  );
};

ListPage.defaultProps = {
  isFetchingItems: false,
  isInfoModalOpen: false,
  isLikedByCurrentUser: false,
  isStaffList: false,
  isMobileApp: false
};

ListPage.propTypes = {
  availabilityByMetadataId: PropTypes.objectOf(displayAvailabilityShape).isRequired,
  bibsByMetadataId: PropTypes.objectOf(displayBibShape),
  closeInfoModal: PropTypes.func.isRequired,
  currentLibrary: libraryShape.isRequired,
  currentUser: userIdentityShape,
  handleCopyCoversClick: PropTypes.func.isRequired,
  handleFlagClick: PropTypes.func.isRequired,
  handleInfoClick: PropTypes.func.isRequired,
  handleLikeClick: PropTypes.func.isRequired,
  handleLoadMoreItemsClick: PropTypes.func.isRequired,
  handlePrintClick: PropTypes.func.isRequired,
  handleShareClick: PropTypes.func.isRequired,
  handleViewFormatsClick: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  isFetchingItems: PropTypes.bool,
  isInfoModalOpen: PropTypes.bool,
  isLikedByCurrentUser: PropTypes.bool,
  isStaffList: PropTypes.bool,
  isMobileApp: PropTypes.bool,
  items: PropTypes.arrayOf(listItemShape).isRequired,
  list: listShape.isRequired,
  manifestationsByMetadataId: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  numLikes: PropTypes.number.isRequired,
  pageTitle: PropTypes.string.isRequired,
  pagination: listPaginationShape.isRequired,
  renderAddToShelfButton: PropTypes.func.isRequired,
  renderUserCard: PropTypes.func.isRequired,
  trackBibAuthorClick: PropTypes.func.isRequired,
  trackBibImageClick: PropTypes.func.isRequired,
  trackItemTitleClick: PropTypes.func.isRequired,
  trackRelatedBibAuthorClick: PropTypes.func.isRequired,
  trackRelatedBibTitleClick: PropTypes.func.isRequired,
  trackWebUrlClick: PropTypes.func.isRequired
};

export default ListPage;
